import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import cx from 'classnames';
import {
    BLOGS_ADD_NEW_POST_PLACEHOLDER,
    FORUM_COMMENT_FORM_EMPTY_MESSAGE_CANNOT_BE_SENT,
    HIDE_REPLIES,
    SEND_COMMENT_FORUM,
    SHOW_IN_CONTESTS,
    VISIBILITY_CHECKBOX_LABEL,
} from 'mk/autogenerated/translations/BlogPostCreateForm.47baf9611cf811a37ac69fd2fc64f7cc'
import { canCreateBlogContests } from 'mk2/apps/blogs/utils';
import { PageRef } from 'mk2/apps/forum/schemas';
import { CheckboxField } from 'mk2/components/forms/CheckboxField';
import { SubmitButton } from 'mk2/components/forms/SubmitButton';
import { TextareaField } from 'mk2/components/forms/TextareaField';
import { Btn, BtnLayout, BtnType } from 'mk2/components/Btn';
import { Form } from 'mk2/components/Form';
import { AutoDialogOpenType, UploadedPhoto } from 'mk2/containers/PhotosUpload/PhotosUpload';
import { ComposeToolbar, FIXED, ToolbarTheme } from 'mk2/containers/Toolbar/Toolbar';
import { ToolbarAdditionalCheckbox } from 'mk2/containers/Toolbar/ToolbarAdditionalCheckbox';
import { mkReduxForm, MKReduxFormInjectedProps } from 'mk2/decorators/mkReduxForm';
import { UserEntity } from 'mk2/schemas';
import React from 'react';
import { FormErrors } from 'redux-form';
import styles from './BlogPostCreateForm.mscss';

export const groupPostCreateFormName = (groupId: number, pageRef: PageRef) => `GROUP_POST_CREATE_FORM_${groupId}_${pageRef}`;

export interface LinkTabItem {
    icon: IconDefinition;
    label: string;
    link?: string;     // undefined = current tab (disabled)
    onClick?: () => void; // custom click handler instead of link
    disabled?: boolean;
}

export interface BlogPostFormData { // BlogPostFormData == BlogsShortMessageFormData
    perex: string;
    photos: UploadedPhoto[];
    sticker: number;

    // fields used in blogs, but not in groups
    publicVisibility?: boolean;
    isContest?: boolean;
    hideReplies?: boolean;
}

interface OwnProps {
    initialValues: BlogPostFormData;
    isInGroups: boolean;
    isIOS: boolean;
    isMobile: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    textareaRef?: (r: HTMLTextAreaElement) => void;

    linkTabs?: LinkTabItem[];

    onSave(values: BlogPostFormData);
}

type Props = OwnProps;

const emojisConfig = {
    fields: [ 'perex' ],
    defaultField: 'perex',
};

const photosConfig = {
    photoType: 'album',
    multiple: true,
    previewPhotoSize: 's165x165',
    showPhotoCode: false,
    autoDialogOpen: AutoDialogOpenType.IF_NO_PHOTOS,
};

class BlogPostCreateFormComponent extends React.Component<Props & MKReduxFormInjectedProps<BlogPostFormData, Props>> {

    public static validate(values: BlogPostFormData): FormErrors<BlogPostFormData> {
        const errors: FormErrors<BlogPostFormData> = {};
        if (!values.perex?.trim()?.length && !values.photos?.length && !values.sticker) {
            errors.perex = FORUM_COMMENT_FORM_EMPTY_MESSAGE_CANNOT_BE_SENT;
        }

        return errors;
    }

    public render() {
        const {
            form, handleSubmit, isInGroups, isMobile, linkTabs,
            requestUser, textareaRef,
        } = this.props;

        const canCreateContests = canCreateBlogContests(requestUser);
        const hasLinkTabs = linkTabs && linkTabs.length > 0;

        return (
            <Form
                className={cx(
                    styles.BlogPostCreateForm,
                    styles['BlogPostCreateForm--create'],
                )}
                onSubmit={handleSubmit}
            >
                {hasLinkTabs && (
                    <div className={styles.BlogPostCreateForm__postTypeTabs}>
                        {linkTabs.map((tab, index) => (
                            <div key={index} className={styles.BlogPostCreateForm__postTypeTabs__item}>
                                <Btn
                                    type={index === 0 ? BtnType.Blue : BtnType.Link}
                                    layout={BtnLayout.IconLabelCount}
                                    link={tab.link}
                                    onClick={tab.onClick}
                                    icon={tab.icon}
                                    label={tab.label}
                                    canBeVisuallyDisabled={false}
                                    disabled={tab.disabled || (!tab.link && !tab.onClick)}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <ComposeToolbar
                    className={cx(
                        styles.BlogPostCreateForm__toolbar,
                        hasLinkTabs && styles['BlogPostCreateForm__toolbar--withLinkTabs'],
                    )}
                    formName={form}
                    emojis={emojisConfig}
                    photos={photosConfig}
                    isFixed={FIXED.NEVER}
                    isInGroups /* true also in blogs; this prop should be refactored to reflect it influence the Toolbar's style */
                    rightAction={
                        <SubmitButton
                            className={cx(
                                styles.BlogPostCreateForm__submit,
                            )}
                            canBeVisuallyDisabled={false}
                            label={SEND_COMMENT_FORUM}
                            formName={form}
                            btnType={BtnType.White}
                        />
                    }
                    additionalActions={!isInGroups && (
                        <>
                            <ToolbarAdditionalCheckbox
                                label={VISIBILITY_CHECKBOX_LABEL}
                                name="publicVisibility"
                                theme={ToolbarTheme.Blue}
                            />
                        </>
                    )}
                    textArea={
                        <div
                            className={styles.BlogPostCreateForm__textareaWrapper}
                        >
                            <div className={styles.BlogPostCreateForm__textareaWrapper__lvl2}>
                                <TextareaField
                                    name="perex"
                                    rows={1}
                                    maxRows={isMobile ? 10 : 25}
                                    placeholder={
                                        // kvoli ios, musime vykrelist ako placeholder={} a nie ako hint={},
                                        // aby bol kurzor dobre zaindentovany
                                        BLOGS_ADD_NEW_POST_PLACEHOLDER}
                                    bottomBorder={false}
                                    marginLeft={false}
                                    textareaRef={textareaRef}
                                    isCompact
                                    scrollDownOnGrow
                                />
                            </div>
                        </div>
                    }
                />
                {canCreateContests && !isInGroups && (
                    <div className={styles.BlogPostCreateForm__contestRelatedFields}>
                        <CheckboxField
                            label={SHOW_IN_CONTESTS}
                            name="isContest"
                        />
                        <CheckboxField
                            label={HIDE_REPLIES}
                            name="hideReplies"
                        />
                    </div>
                )}
            </Form>
        );
    }
}

export const BlogPostCreateFormFactory = (formName: string) => mkReduxForm<BlogPostFormData, Props>({
    formName,
    photosConfig,
    onValidate: (values) => BlogPostCreateFormComponent.validate(values),
    onSave: (values, props) => props.onSave(values),
})(BlogPostCreateFormComponent);
